/**
 * sync.ts
 *
 * 離線操作佇列同步引擎。
 *
 * 功能：
 * - syncOnce()     : 一次性同步所有 pending 佇列條目（含重試與衝突偵測）
 * - startAutoSync() : 啟動定時自動同步（60 秒間隔），並監聽 online 事件立即觸發
 * - stopAutoSync()  : 停止定時同步
 *
 * 傳輸方式：
 * - 優先走後端批量端點 POST /api/sync/batch（≤100 條/次，單次往返），
 *   按 localId 對應逐條結果；批量請求本身失敗（網絡/舊後端無此端點）時
 *   回落為逐條直發原始請求，保證可用性。
 * - 帶自定義 headers 的條目一律逐條直發（batch 端點不轉發自定義 headers）。
 * - 出隊按 createdAt 升序，保證「先建立、後修改」的操作時序。
 *
 * 重試策略：
 * - 每次失敗 attempts++，超過 MAX_ATTEMPTS(5) 後將條目標記為 'failed'（不再重試）
 * - HTTP 409 衝突回應將條目立即標記為 'failed'（lastError 帶 'Conflict: ' 前綴，
 *   供 FailedOutboxList 識別）並加入 conflicts 清單
 */
import {
  claimPendingOutbox,
  deleteOutbox,
  getOfflineDb,
  listPendingOutbox,
  reapInflightOutbox,
  type OutboxEntry,
} from '@/pwa/db';
import { flushPendingUploads } from '@/pwa/pending-upload-worker';

/** 最大重試次數（超過後永久標記為 failed） */
const MAX_ATTEMPTS = 5;

/** 定時同步間隔（毫秒） */
const SYNC_INTERVAL_MS = 60_000;

/** 批量同步端點（後端逐條內部分發並回傳每條結果） */
const BATCH_ENDPOINT = '/api/sync/batch';

/** 批量端點單次請求的條目上限（與後端 zod schema 的 max(100) 對齊） */
const BATCH_LIMIT = 100;

let syncInFlight: Promise<SyncResult> | null = null;

// ─────────────────────────────────────────────
// 公開類型
// ─────────────────────────────────────────────

/**
 * 一次同步批次的結果摘要。
 */
export interface SyncResult {
  /** 本次成功同步的條目數 */
  success: number;
  /** 本次失敗（含衝突）的條目數 */
  failed: number;
  /** 遭遇 409 衝突的條目列表（需用戶手動解決） */
  conflicts: OutboxEntry[];
}

// ─────────────────────────────────────────────
// 核心同步函數
// ─────────────────────────────────────────────

/**
 * 執行一次性同步：遍歷所有 pending 條目並依序嘗試發送請求。
 *
 * 流程：
 * 1. 讀取所有 pending 條目
 * 2. 每筆先標記為 inflight
 * 3. 發送 HTTP 請求
 *    - 成功（res.ok && json.success !== false）：刪除條目
 *    - 409 衝突：標記為 failed，加入 conflicts
 *    - 其他失敗：bump attempts，超過上限則永久 failed
 * 4. 返回 SyncResult
 *
 * @returns 本次同步結果摘要
 */
export async function syncOnce(): Promise<SyncResult> {
  if (syncInFlight) return syncInFlight;
  syncInFlight = syncOnceInternal().finally(() => {
    syncInFlight = null;
  });
  return syncInFlight;
}

async function syncOnceInternal(): Promise<SyncResult> {
  // 阶段 2：离线上传 worker 先跑一遍，让带负数 tempId 的草稿能被替换成 realId
  // 之后再尝试同步 outbox。flush 失败也不影响后续：还是负数的条目本轮就跳过。
  try {
    await flushPendingUploads();
  } catch (err) {
    console.warn('[sync] flushPendingUploads failed', err);
  }

  // outbox 主鍵是 UUID，索引掃描順序等同隨機；按 createdAt 升序出隊，
  // 保證離線期間「先 POST 建立、後 PATCH 修改」的操作按時序重放。
  const pending = (await listPendingOutbox()).sort((a, b) => a.createdAt - b.createdAt);

  // 先全部 claim（原子 pending→inflight，防多 tab 重複發送），再統一發送
  const claimedEntries: OutboxEntry[] = [];
  for (const entry of pending) {
    const claimed = await claimPendingOutbox(entry.id);
    if (!claimed) continue;

    // 跳过 attachments 仍含负数 tempId 的 form_instance 条目（worker 还没换完）
    if (claimed.entityType === 'form_instance' && bodyHasNegativeAttachments(claimed.body)) {
      await bumpAttempts(claimed.id, claimed.attempts, 'Waiting for pending attachments');
      continue;
    }
    claimedEntries.push(claimed);
  }

  const result: SyncResult = { success: 0, failed: 0, conflicts: [] };

  // 帶自定義 headers 的條目 batch 端點無法轉發，留給逐條直發
  const individualEntries = claimedEntries.filter(
    (e) => e.headers && Object.keys(e.headers).length > 0,
  );
  const batchableEntries = claimedEntries.filter(
    (e) => !e.headers || Object.keys(e.headers).length === 0,
  );

  // 批量發送（每 chunk ≤ BATCH_LIMIT）；批量請求本身失敗的 chunk 回落逐條直發
  for (let i = 0; i < batchableEntries.length; i += BATCH_LIMIT) {
    const chunk = batchableEntries.slice(i, i + BATCH_LIMIT);
    const handled = await sendChunkViaBatch(chunk, result);
    if (!handled) individualEntries.push(...chunk);
  }

  for (const entry of individualEntries) {
    await sendEntryIndividually(entry, result);
  }

  return result;
}

/**
 * 把一批條目經 POST /api/sync/batch 一次送出，並按 localId 逐條落地結果。
 *
 * @returns true 表示批量請求已受理（無論條目各自成敗都已處理完畢）；
 *          false 表示批量請求本身失敗（網絡錯誤 / 端點不存在 / 5xx），
 *          條目未被處理，呼叫端應回落逐條直發。
 */
async function sendChunkViaBatch(chunk: OutboxEntry[], result: SyncResult): Promise<boolean> {
  if (chunk.length === 0) return true;

  let results: Array<{
    localId: string;
    status: 'success' | 'conflict' | 'error';
    statusCode?: number;
    message?: string;
  }>;
  try {
    const res = await fetch(BATCH_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        operations: chunk.map((e) => ({
          localId: e.id,
          method: e.method,
          url: e.url,
          body: e.body,
        })),
      }),
    });
    const json = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      data?: { results?: typeof results };
    };
    if (!res.ok || json.success === false || !Array.isArray(json.data?.results)) {
      console.warn(`[sync] batch endpoint unavailable (HTTP ${res.status}), fallback to per-entry`);
      return false;
    }
    results = json.data.results;
  } catch (err) {
    // 批量請求整體網絡失敗：條目仍為 inflight，回落逐條路徑處理（含重試計數）
    console.warn('[sync] batch request failed, fallback to per-entry', err);
    return false;
  }

  const byLocalId = new Map(results.map((r) => [r.localId, r]));
  for (const entry of chunk) {
    const r = byLocalId.get(entry.id);
    if (!r) {
      // 後端未回該條結果（異常防禦）：保留 pending 下輪重試
      await bumpAttempts(entry.id, entry.attempts + 1, 'Batch response missing result');
      result.failed++;
      continue;
    }
    if (r.status === 'success') {
      await deleteOutbox(entry.id);
      result.success++;
      continue;
    }
    if (r.status === 'conflict') {
      result.conflicts.push(entry);
      await markStatus(entry.id, 'failed', `Conflict: ${r.message ?? 'version mismatch'}`);
      result.failed++;
      continue;
    }
    // status === 'error'：按重試策略累加
    const attempts = entry.attempts + 1;
    const errMsg = r.message ?? `HTTP ${r.statusCode ?? '?'}`;
    if (attempts >= MAX_ATTEMPTS) {
      await markStatus(entry.id, 'failed', errMsg);
    } else {
      await bumpAttempts(entry.id, attempts, errMsg);
    }
    result.failed++;
  }
  return true;
}

/**
 * 逐條直發單一條目（batch 回落路徑 / 帶自定義 headers 的條目）。
 * 成功刪除條目；409 標 failed 進 conflicts；其他失敗按重試策略累加。
 */
async function sendEntryIndividually(entry: OutboxEntry, result: SyncResult): Promise<void> {
  try {
    const res = await fetch(entry.url, {
      method: entry.method,
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(entry.headers ?? {}),
      },
      body: entry.body !== undefined ? JSON.stringify(entry.body) : undefined,
    });

    // 解析回應 JSON（失敗時降級為空物件）
    const json = await res.json().catch(() => ({}));

    // 409 衝突：版本不符或業務衝突，需人工介入
    if (res.status === 409) {
      result.conflicts.push(entry);
      await markStatus(
        entry.id,
        'failed',
        `Conflict: ${(json as { error?: { message?: string } }).error?.message ?? 'version mismatch'}`,
      );
      result.failed++;
      return;
    }

    // 其他非成功回應或 API 返回 success: false
    if (!res.ok || (json as { success?: boolean }).success === false) {
      const attempts = entry.attempts + 1;
      const errMsg =
        (json as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`;
      if (attempts >= MAX_ATTEMPTS) {
        await markStatus(entry.id, 'failed', errMsg);
      } else {
        await bumpAttempts(entry.id, attempts, errMsg);
      }
      result.failed++;
      return;
    }

    // 成功：從佇列刪除
    await deleteOutbox(entry.id);
    result.success++;
  } catch (err) {
    // 網絡錯誤、JSON 解析失敗等例外
    const attempts = entry.attempts + 1;
    const msg = err instanceof Error ? err.message : String(err);
    if (attempts >= MAX_ATTEMPTS) {
      await markStatus(entry.id, 'failed', msg);
    } else {
      await bumpAttempts(entry.id, attempts, msg);
    }
    result.failed++;
  }
}

// ─────────────────────────────────────────────
// 私有輔助函數
// ─────────────────────────────────────────────

/**
 * 更新佇列條目的 status（及可選的錯誤訊息）。
 *
 * @param id     - 條目 UUID
 * @param status - 目標狀態
 * @param error  - 可選的錯誤說明
 */
async function markStatus(
  id: string,
  status: OutboxEntry['status'],
  error?: string,
): Promise<void> {
  const db = await getOfflineDb();
  const cur = await db.get('outbox', id);
  if (!cur) return;
  cur.status = status;
  if (error) cur.lastError = error;
  await db.put('outbox', cur);
}

/**
 * 在保留 pending 狀態的情況下，累加 attempts 並記錄最後錯誤。
 * 下次同步週期會繼續嘗試。
 *
 * @param id       - 條目 UUID
 * @param attempts - 新的嘗試次數（已 +1）
 * @param error    - 本次失敗的錯誤訊息
 */
async function bumpAttempts(id: string, attempts: number, error: string): Promise<void> {
  const db = await getOfflineDb();
  const cur = await db.get('outbox', id);
  if (!cur) return;
  cur.attempts = attempts;
  cur.status = 'pending'; // 保持 pending 以便下次同步
  cur.lastError = error;
  await db.put('outbox', cur);
}

/**
 * 检查 form-instance PATCH body 内 data 字段是否还含负数 fileId。
 * 含则本轮跳过，等 pending-upload-worker 完成替换后下一轮再尝试。
 */
function bodyHasNegativeAttachments(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const data = (body as { data?: Record<string, unknown> }).data;
  if (!data || typeof data !== 'object') return false;

  for (const v of Object.values(data)) {
    // file/photo 字段：number[]
    if (Array.isArray(v) && v.some((x) => typeof x === 'number' && x < 0)) {
      return true;
    }
    // checklist 字段：{ [clauseId]: { attachments: number[] } }
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      for (const cv of Object.values(v as Record<string, unknown>)) {
        if (cv && typeof cv === 'object' && 'attachments' in cv) {
          const att = (cv as { attachments?: unknown }).attachments;
          if (Array.isArray(att) && att.some((x) => typeof x === 'number' && x < 0)) {
            return true;
          }
        }
      }
    }
  }
  return false;
}

// ─────────────────────────────────────────────
// 自動同步控制
// ─────────────────────────────────────────────

/** setInterval handle，null 表示未啟動 */
let intervalId: number | null = null;

/**
 * 啟動定時自動同步（60 秒間隔）。
 * 同時監聽 `online` 事件，網絡恢復後立即觸發一次同步。
 * 多次調用安全（已啟動則忽略）。
 *
 * @param onResult - 可選的同步完成回調，接收 SyncResult 摘要
 */
export function startAutoSync(onResult?: (r: SyncResult) => void): void {
  // 防止重複啟動
  if (intervalId !== null) return;

  // 上一次会话若在 fetch 中途 crash / tab 关闭，会留下卡死的 inflight 孤儿；
  // 启动时一次性捞回为 pending，由后续 syncOnce 重试（PR #2 review #5）
  reapInflightOutbox().catch((err) => {
    console.error('[sync] reapInflightOutbox failed', err);
  });

  intervalId = window.setInterval(() => {
    if (!navigator.onLine) return; // 離線時跳過
    void syncOnce().then((r) => onResult?.(r));
  }, SYNC_INTERVAL_MS);

  // 網絡恢復時立即同步（不等待下一個 interval）
  window.addEventListener('online', () => {
    void syncOnce().then((r) => onResult?.(r));
  });
}

/**
 * 停止定時自動同步。
 * 注意：不移除 `online` 事件監聽器（設計上這是長期存在的）。
 */
export function stopAutoSync(): void {
  if (intervalId !== null) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
}
